import { ShapeHandler } from "./shape-handler.interface";
import { Annotation } from "../models/annotation.model";

/**
 * Handler for text-specific behavior
 * Implements the ShapeHandler interface for text annotations
 */
export class TextHandler implements ShapeHandler {
  /**
   * Gets the left position (percentage) of a text annotation
   */
  getLeft(annotation: Annotation): number {
    return (annotation as any).x;
  }

  /**
   * Gets the top position (percentage) of a text annotation
   */
  getTop(annotation: Annotation): number {
    return (annotation as any).y;
  }

  /**
   * Gets the width (percentage) of a text annotation
   * For text, we'll use a smaller default width since we want it to be just wide enough for the text
   */
  getWidth(annotation: Annotation): number {
    // Return a much smaller width - text content will determine the actual display width
    return (annotation as any).width || 5; // Smaller default minimum width
  }

  /**
   * Gets the height (percentage) of a text annotation
   * For text, we'll use a smaller default height since we want it to be just tall enough for the text
   */
  getHeight(annotation: Annotation): number {
    // Return a much smaller height - text content will determine the actual display height
    return (annotation as any).height || 3; // Smaller default minimum height
  }

  /**
   * Gets the padding-bottom value for text (always null)
   */
  getPaddingBottom(annotation: Annotation): number | null {
    return null; // Text doesn't use padding-bottom
  }

  /**
   * Updates a text annotation during drag operations based on the drag mode
   */
  updateOnDrag(
    dragMode: string,
    annotation: any,
    dragStart: {
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    },
    deltaXPercent: number,
    deltaYPercent: number,
    minSizePercent: number
  ): any {
    const result = { ...annotation };

    if (dragMode === "whole") {
      // Move the entire text annotation - for text, allow positioning closer to the edge
      // Since text is centered with transform: translate(-50%, -50%), we can go right to the edge
      result.x = Math.max(0, Math.min(100, (dragStart.x || 0) + deltaXPercent));
      result.y = Math.max(0, Math.min(100, (dragStart.y || 0) + deltaYPercent));
    } else if (dragMode === "resize") {
      // Resize text by dragging up/down
      // 1. Calculate new height based on vertical movement with increased sensitivity
      // This primarily affects the font size via the height factor
      // Multiply deltaYPercent by 2.5 to make resizing more sensitive
      const newHeight = Math.max(minSizePercent, (dragStart.height || 5) - deltaYPercent * 2.5);

      // 2. For text, we keep a flexible width since the text container auto-sizes
      // Use a width based on original width to maintain proportions, but allow for expansion
      // The container will automatically expand further if the text is longer
      const newWidth = Math.max(minSizePercent, dragStart.width || 20);

      // 3. DO NOT adjust vertical position - this was causing the text to move up/down during resize
      // Keep the text anchored at its original position
      result.y = dragStart.y || 0;

      // 4. Update height and width
      result.height = newHeight;
      result.width = newWidth;

      // 5. For text, ensure height has reasonable limits to avoid extreme font sizes
      // This affects the --height-factor CSS variable calculation
      if (newHeight < 5) {
        result.height = 5; // Minimum height to ensure text is visible
      } else if (newHeight > 80) {
        result.height = 80; // Maximum height
      }
    }

    // For text annotations, we can allow them to be positioned closer to edges
    // Since we're using transform: translate(-50%, -50%), we can allow center point to go right to the edge
    result.x = Math.max(0, Math.min(100, result.x));
    result.y = Math.max(0, Math.min(100, result.y));

    // Still maintain minimum and maximum constraints for width/height
    result.width = Math.max(minSizePercent, Math.min(100, result.width));
    result.height = Math.max(minSizePercent, Math.min(100, result.height));

    return result;
  }

  /**
   * Converts a text annotation to points array for storage
   */
  toPoints(annotation: any): { x: number; y: number }[] {
    return [
      { x: annotation.x, y: annotation.y }, // Position point
      { x: annotation.x + (annotation.width || 10), y: annotation.y + (annotation.height || 5) } // Size reference point
    ];
  }
}
